import { Component, computed, effect, inject } from '@angular/core';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { RestService, stripQueryParams } from '@cumlaude/shared-services';
import { Router } from '@angular/router';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { FavorietDialogResultaat, FavorietDialogComponent } from '../../favorieten/favoriet-dialog/favoriet-dialog.component';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { NotifiableError } from '@bugsnag/js';
import { BugsnagService } from '@cumlaude/bugsnag';
import { DatePipe } from '@angular/common';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { BaseDialogComponent, DialogHeaderComponent } from '@cumlaude/shared-components-dialogs';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { RCumLaudeAccount } from '@cumlaude/service-contract';
import { AutorisatieService } from '../../../services/autorisatie.service';
import { GedeeldePaginaPersoonGroep, GedeeldePaginaPersoonMode } from '../../../startpagina/berichten/gedeelde-pagina-persoon.model';
import { UserService } from '../../../services/user.service';
import { DashboardNavigatieService } from '../../../services/dashboard-navigatie.service';
import { GtmService } from '../../../services/gtm.service';
import { PaginaSamenvattingComponent } from '../../../shared/components/pagina-samenvatting/pagina-samenvatting.component';

@Component({
	selector: 'app-gedeelde-pagina-dialog',
	templateUrl: './gedeelde-pagina-dialog.component.html',
	styleUrls: ['./gedeelde-pagina-dialog.component.scss'],
	imports: [BaseDialogComponent, DialogHeaderComponent, ButtonComponent, DatePipe, IconComponent, PaginaSamenvattingComponent],
})
export class GedeeldePaginaDialogComponent {
	protected readonly autorisatieService = inject(AutorisatieService);
	protected readonly restService = inject(RestService);
	private readonly dialog = inject(Dialog);
	protected readonly router = inject(Router);
	protected readonly toastr = inject(ToastrService);
	readonly dialogRef = inject<DialogRef<void>>(DialogRef);
	private readonly bugsnag = inject(BugsnagService);
	private readonly userService = inject(UserService);
	private readonly dashboardNavigatie = inject(DashboardNavigatieService);
	private readonly gtm = inject(GtmService);

	private readonly data = inject<{
		gedeeldePaginaPersoon: GedeeldePaginaPersoonGroep;
	}>(DIALOG_DATA);

	readonly gedeeldePaginaPersoonGroep = computed(() => this.data.gedeeldePaginaPersoon);

	readonly title = computed(() =>
		this.gedeeldePaginaPersoonGroep().type === GedeeldePaginaPersoonMode.INBOX ? 'Ontvangen bericht' : 'Verzonden bericht'
	);

	readonly gedeeldePaginaPersoon = computed(() => this.gedeeldePaginaPersoonGroep().primary);

	readonly url = computed(() => this.gedeeldePaginaPersoon().state);

	readonly bericht = computed(() => this.gedeeldePaginaPersoon().bericht);

	readonly enabled = computed(() => this.autorisatieService.isUrlAllowed(stripQueryParams(this.url())));

	constructor() {
		effect(async () => {
			await this.markeerGelezen();
		});
	}

	private async markeerGelezen() {
		if (this.data.gedeeldePaginaPersoon.type !== GedeeldePaginaPersoonMode.INBOX) return;

		const gedeeldePaginaPersoon = this.gedeeldePaginaPersoon();
		if (gedeeldePaginaPersoon.gelezen) return;

		try {
			await firstValueFrom(this.restService.putGedeeldePaginaPersoon({ ...gedeeldePaginaPersoon, gelezen: true }));
		} catch (e) {
			this.bugsnag.notify(<NotifiableError>e);
		}
	}

	openOverzicht() {
		const bestemming = this.url();
		const groep = this.gedeeldePaginaPersoonGroep();
		this.dashboardNavigatie.track('inbox', bestemming, groep.type === GedeeldePaginaPersoonMode.INBOX ? 'inbox' : 'verzonden');
		this.router.navigateByUrl(bestemming);
		this.dialogRef.close();
	}

	async opslaanAlsFavoriet() {
		const mappen = await lastValueFrom(this.restService.getFavorietMappen());

		const account = this.userService.account();
		if (!account) return;

		const template = this.getNieuweMapTemplate(account);

		this.dialogRef.close();
		const dialogRef = this.dialog.open<FavorietDialogResultaat>(FavorietDialogComponent, {
			data: { template, mappen, url: this.url() },
		});

		const result = await lastValueFrom(dialogRef.closed);
		if (!result) return;

		const { favoriet, nieuweMappen } = result;

		try {
			const persistedNieuweMappen = await lastValueFrom(this.restService.postFavorietMappen(nieuweMappen));
			const nieuweMapIndex = favoriet.map ? nieuweMappen.indexOf(favoriet.map) : -1;
			if (nieuweMapIndex >= 0) favoriet.map = persistedNieuweMappen[nieuweMapIndex];

			await lastValueFrom(this.restService.postFavoriet(favoriet));
			this.gtm.pushEvent('favoriet_aanmaken', { bestemming: stripQueryParams(favoriet.state), startpunt: 'gedeelde_pagina' });
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error(`Er ging iets fout bij het wegschrijven.`);
		}
	}

	getNieuweMapTemplate(account: RCumLaudeAccount) {
		return { $type: 'favoriet.RFavorietMap', naam: 'Nieuwe map', account, instelling: account.instelling };
	}

	protected readonly GedeeldePaginaPersoonMode = GedeeldePaginaPersoonMode;
}
